import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { QuickEditSheet } from "../components/QuickEditSheet";
import { StatusBadge } from "../components/StatusBadge";
import { formatExpirationMonth } from "../lib/expiration";
import { buildRelatedRowMeta, relatedRowClass } from "../lib/relatedRows";
import { groupItemsForBagDisplay } from "../lib/qrsJumpKitLocations";
import { actionReasonAuditNote, REMOVE_REASON_OPTIONS, type RemoveReason } from "../lib/removeReasons";
import type { BagInventoryItem, StockLotChoice } from "../lib/types";
import { useRegisterViewRefresh, useUnsavedChangesWarning } from "../lib/viewRefresh";

function useDebounced(value: string, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function showBagInNavigator(name: string) {
  const normalized = name.trim().toLowerCase();
  return !["extra", "rescue task force", "stop the bleed kit"].includes(normalized);
}

function bagLabel(name: string) {
  if (name.trim().toLowerCase() === "qrs bag") return "QRS Jump Kit";
  return name;
}

function bagItemRowKey(item: BagInventoryItem, index: number): string {
  return item.recordId ?? `missing_${item.catalogItemId}_${index}`;
}

export function isBagRouteReady(requestedBagId: string, loadedBagId: string | null | undefined): boolean {
  return Boolean(requestedBagId) && requestedBagId === loadedBagId;
}

interface BagContext {
  id: string;
  name: string;
  apparatusId: string;
  apparatusName: string;
  complianceScope: "qrs" | "non_qrs";
}

interface NavigatorOption {
  apparatusId: string;
  apparatusName: string;
  bags: Array<{ id: string; name: string }>;
}

export function BagDetailScreen({ enteredName, rememberName }: { enteredName: string; rememberName: (name: string) => void }) {
  const { bagId = "" } = useParams();
  const navigate = useNavigate();
  const [bag, setBag] = useState<BagContext | null>(null);
  const [items, setItems] = useState<BagInventoryItem[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<BagInventoryItem | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedStockLots, setSelectedStockLots] = useState<StockLotChoice[]>([]);
  const [selectedStockLotsLoading, setSelectedStockLotsLoading] = useState(false);

  const [navigatorRawOptions, setNavigatorRawOptions] = useState<NavigatorOption[]>([]);
  const [navApparatusId, setNavApparatusId] = useState("");
  const [navBagId, setNavBagId] = useState("");
  const requestIdRef = useRef(0);
  const requestAbortRef = useRef<AbortController | null>(null);

  const debouncedSearch = useDebounced(search);
  const routeReady = isBagRouteReady(bagId, bag?.id);
  const routeBag = routeReady ? bag : null;
  const routeItems = routeReady ? items : [];
  const routeSelected = routeReady ? selected : null;

  const refresh = async () => {
    if (!bagId) return;
    const requestId = ++requestIdRef.current;
    requestAbortRef.current?.abort();
    const controller = new AbortController();
    requestAbortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const result = await api.getBagInventory(bagId, debouncedSearch, {
        signal: controller.signal,
        dedupe: false
      });
      if (requestId !== requestIdRef.current) return;
      setBag(result.bag);
      setItems(result.items);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      throw err;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (search !== debouncedSearch) {
      requestIdRef.current += 1;
      requestAbortRef.current?.abort();
      setLoading(true);
      return;
    }
    refresh().catch((err: ApiError) => setError(err.message));
    return () => requestAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bagId, search, debouncedSearch]);

  useEffect(() => {
    setBag(null);
    setItems([]);
    setSelected(null);
    setNotice("");
  }, [bagId]);

  const revalidateAfterSave = async () => {
    setNotice("Saved.");
    try {
      await refresh();
    } catch {
      setNotice("Saved. Latest kit data could not be refreshed; use Refresh when the connection improves.");
    }
  };

  useEffect(() => {
    if (!routeSelected) {
      setSelectedStockLots([]);
      setSelectedStockLotsLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setSelectedStockLotsLoading(true);

    api
      .getStockLotsForCatalogItem(routeSelected.catalogItemId, {
        signal: controller.signal,
        dedupe: false
      })
      .then((result) => {
        if (!active || controller.signal.aborted) return;
        setSelectedStockLots(result.items);
      })
      .catch(() => {
        if (!active || controller.signal.aborted) return;
        setSelectedStockLots([]);
      })
      .finally(() => {
        if (!active || controller.signal.aborted) return;
        setSelectedStockLotsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [routeSelected]);

  const grouped = useMemo(() => {
    return groupItemsForBagDisplay(routeBag?.name, routeItems);
  }, [routeBag?.name, routeItems]);

  const relatedMetaByKey = useMemo(
    () =>
      buildRelatedRowMeta(
        routeItems.map((item, index) => ({
          rowKey: bagItemRowKey(item, index),
          catalogItemId: item.catalogItemId
        }))
      ),
    [routeItems]
  );

  const navigatorOptions = useMemo(
    () =>
      navigatorRawOptions
        .map((entry) => ({
          ...entry,
          bags: entry.bags.filter((entryBag) => showBagInNavigator(entryBag.name) || entryBag.id === bagId)
        }))
        .filter((entry) => entry.bags.length > 0),
    [navigatorRawOptions, bagId]
  );

  const selectedNavBags = useMemo(
    () => navigatorOptions.find((entry) => entry.apparatusId === navApparatusId)?.bags ?? [],
    [navigatorOptions, navApparatusId]
  );

  const updateItem = async (
    item: BagInventoryItem,
    payload: {
      enteredName: string;
      quantity: number;
      expirationDate: string;
      notes: string;
      sourceType?: "ambulance" | "hospital" | "other" | "stock" | "";
      stockLotId?: string;
      stockTransferQuantity?: number;
      actionReason?: RemoveReason | "";
      removeReason?: RemoveReason | "";
    }
  ) => {
    const name = payload.enteredName.trim();
    if (!name) {
      throw new Error("Your Name is required.");
    }
    rememberName(name);
    const actionNote = actionReasonAuditNote(payload.actionReason ?? payload.removeReason);
    if (!actionNote) {
      throw new Error("Reason is required.");
    }

    if (payload.sourceType === "stock") {
      const stockLotId = (payload.stockLotId ?? "").trim();
      const moveQuantity = Math.max(0, Math.round(payload.stockTransferQuantity ?? 0));
      if (!stockLotId) {
        throw new Error("Choose a stock item.");
      }
      if (moveQuantity <= 0) {
        throw new Error("Transfer quantity must be at least 1.");
      }

      const action = item.recordId && item.quantity > 0 ? "replace_item" : "add_item";
      await api.applyApparatusItemAction({
        action,
        bagId,
        catalogItemId: item.catalogItemId,
        quantity: moveQuantity,
        expirationDate: null,
        notes: payload.notes,
        sourceType: "stock",
        stockLotId,
        replaceRecordId: action === "replace_item" ? item.recordId : null,
        enteredName: name,
        note: actionNote
      });

      await revalidateAfterSave();
      return;
    }

    const sourceType = payload.sourceType ? payload.sourceType : null;

    if (item.recordId) {
      await api.updateInventoryRecord({
        recordId: item.recordId,
        quantity: payload.quantity,
        expirationDate: payload.expirationDate || null,
        notes: payload.notes,
        sourceType,
        note: actionNote,
        enteredName: name
      });
    } else {
      await api.addInventoryRecord({
        bagId,
        catalogItemId: item.catalogItemId,
        quantity: payload.quantity,
        expirationDate: payload.expirationDate || null,
        notes: payload.notes,
        sourceType,
        enteredName: name,
        note: actionNote
      });
    }

    await revalidateAfterSave();
  };

  const addExpirationForItem = async (
    item: BagInventoryItem,
    payload: {
      enteredName: string;
      quantity: number;
      expirationDate: string;
      notes: string;
      sourceType?: "ambulance" | "hospital" | "other" | "stock" | "";
      stockLotId?: string;
      stockTransferQuantity?: number;
      actionReason?: RemoveReason | "";
    }
  ) => {
    const name = payload.enteredName.trim();
    if (!name) {
      throw new Error("Your Name is required.");
    }
    rememberName(name);
    const actionNote = actionReasonAuditNote(payload.actionReason);
    if (!actionNote) {
      throw new Error("Reason is required.");
    }

    const sourceType = payload.sourceType;
    if (!sourceType) {
      throw new Error("Source is required when splitting this item.");
    }

    const quantity = Math.max(0, Math.round(payload.quantity));
    if (quantity <= 0) {
      throw new Error("Quantity must be at least 1.");
    }

    if (sourceType === "stock") {
      const stockLotId = (payload.stockLotId ?? "").trim();
      if (!stockLotId) {
        throw new Error("Choose a stock item.");
      }

      await api.applyApparatusItemAction({
        action: "add_expiration",
        bagId,
        catalogItemId: item.catalogItemId,
        quantity,
        expirationDate: null,
        notes: payload.notes,
        sourceType: "stock",
        stockLotId,
        replaceRecordId: null,
        enteredName: name,
        note: actionNote
      });

      await revalidateAfterSave();
      return;
    }

    if (!item.recordId) {
      throw new Error("Open an existing item before splitting.");
    }

    await api.splitInventoryRecord({
      recordId: item.recordId,
      splitQuantity: quantity,
      targetExpirationDate: payload.expirationDate,
      targetNotes: payload.notes || null,
      sourceType,
      enteredName: name,
      note: actionNote
    });

    await revalidateAfterSave();
  };

  const loadNavigatorOptions = async () => {
    if (navigatorRawOptions.length > 0) return navigatorRawOptions;
    const directory = await api.getApparatusDirectory();
    const options = directory.apparatus
      .map((entry) => ({
        apparatusId: entry.id,
        apparatusName: entry.name,
        bags: entry.bags
      }))
      .filter((entry) => entry.bags.length > 0);
    setNavigatorRawOptions(options);
    return options;
  };

  useEffect(() => {
    if (!routeBag?.apparatusId) return;
    loadNavigatorOptions().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeBag?.apparatusId]);

  useEffect(() => {
    if (!routeBag?.apparatusId) return;
    setNavApparatusId(routeBag.apparatusId);
    setNavBagId(bagId);
  }, [routeBag?.apparatusId, bagId]);

  const navigateToBag = (targetBagId: string) => {
    if (!targetBagId || targetBagId === bagId) return;
    navigate(`/bags/${targetBagId}`);
  };

  const selectNavigatorApparatus = (apparatusId: string) => {
    setNavApparatusId(apparatusId);
    const nextBags = navigatorOptions.find((entry) => entry.apparatusId === apparatusId)?.bags ?? [];
    const currentKitName = (routeBag?.name ?? "").trim().toLowerCase();
    const nextBag =
      nextBags.find((entryBag) => entryBag.name.trim().toLowerCase() === currentKitName)?.id ??
      nextBags[0]?.id ??
      "";
    setNavBagId(nextBag);
    if (nextBag) navigateToBag(nextBag);
  };

  const selectNavigatorBag = (targetBagId: string) => {
    setNavBagId(targetBagId);
    navigateToBag(targetBagId);
  };

  const editorOpen = Boolean(routeSelected);
  useUnsavedChangesWarning(editorOpen);
  useRegisterViewRefresh(refresh, {
    disabled: !routeReady || editorOpen || loading,
    disabledReason: editorOpen
      ? "Finish or close the item editor before refreshing."
      : "This kit is already updating."
  });

  if (!routeReady || !routeBag) {
    return <p className={error ? "error-text" : "muted"}>{error || "Loading kit…"}</p>;
  }

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      {notice ? <p className="muted small">{notice}</p> : null}
      <section className="card stack-sm">
        <div className="row-between">
          <div>
            <h2>{bagLabel(routeBag.name)}</h2>
            <p className="muted">
              <Link
                className="inline-link"
                to={`/apparatus/${routeBag.apparatusId}`}
                aria-label={`Back to ${routeBag.apparatusName}`}
              >
                {routeBag.apparatusName}
              </Link>
            </p>
          </div>
          {loading ? (
            <p className="muted small" role="status">
              Updating…
            </p>
          ) : null}
        </div>

        {navigatorOptions.length > 0 ? (
          <div className="card-soft bag-nav-card stack-xs">
            <div className="bag-nav-grid">
              <label className="stack-xs">
                <span className="label">Apparatus</span>
                <select
                  className="input"
                  value={navApparatusId || navigatorOptions[0]?.apparatusId || ""}
                  onChange={(event) => selectNavigatorApparatus(event.target.value)}
                >
                  {navigatorOptions.map((option) => (
                    <option key={option.apparatusId} value={option.apparatusId}>
                      {option.apparatusName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stack-xs">
                <span className="label">Kit</span>
                <select className="input" value={navBagId || selectedNavBags[0]?.id || ""} onChange={(event) => selectNavigatorBag(event.target.value)}>
                  {selectedNavBags.map((entryBag) => (
                    <option key={entryBag.id} value={entryBag.id}>
                      {bagLabel(entryBag.name)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ) : null}

        <input className="input" placeholder="Search items" value={search} onChange={(event) => setSearch(event.target.value)} />
      </section>

      {grouped.map((group) => (
        <section key={group.name || routeBag.id} className="card stack-xs">
          {group.name ? <h3>{group.name}</h3> : null}
          <div className="list">
            {group.items.map((item) => {
              const itemIndex = routeItems.indexOf(item);
              const rowKey = bagItemRowKey(item, itemIndex);
              const relatedMeta = relatedMetaByKey[rowKey];
              return (
                <button key={rowKey} className={`list-item ${relatedRowClass(relatedMeta)}`} onClick={() => setSelected(item)}>
                  <div>
                    <div className="row gap-sm search-result-meta">
                      <strong className="text-wrap-anywhere">{item.itemName}</strong>
                    </div>
                    <p className="muted small">
                      Qty {item.quantity}
                      {item.certificationRequired
                        ? ` • Required: ${item.apparatusRequiredQty}`
                        : item.alertOnly
                          ? ` • Alert: ${item.apparatusRequiredQty}`
                          : " • Non-cert"}
                      {item.expirationDate ? ` • Exp ${formatExpirationMonth(item.expirationDate)}` : ""}
                    </p>
                    {item.catalogItemId === "cat_blanket" && item.notes ? (
                      <p className="muted small">{item.notes}</p>
                    ) : null}
                  </div>
                  <StatusBadge severity={item.status.severity} text={item.status.reason ?? undefined} />
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {routeSelected ? (
        <QuickEditSheet
          open
          resetKey={`${routeSelected.recordId ?? "missing"}:${routeSelected.catalogItemId}:${routeSelected.expirationDate ?? ""}:${routeSelected.updatedAt ?? ""}`}
          title={routeSelected.itemName}
          presentation="modal"
          showNotes={false}
          sourceMode="apparatus"
          requireActionReason
          actionReasonOptions={REMOVE_REASON_OPTIONS}
          requireRemoveReason={false}
          initial={{
            enteredName,
            quantity: routeSelected.quantity,
            expirationDate: routeSelected.expirationDate ?? "",
            notes: routeSelected.notes ?? "",
            sourceType: "",
            stockLotId: "",
            stockTransferQuantity: Math.max(1, routeSelected.quantity || 1)
          }}
          stockLots={selectedStockLots}
          stockLotsLoading={selectedStockLotsLoading}
          isExpiringItem={routeSelected.isExpiring}
          onClose={() => setSelected(null)}
          onRemove={
            routeSelected.recordId
              ? (value) =>
                  updateItem(routeSelected, {
                    ...value,
                    quantity: 0,
                    expirationDate: ""
                  })
              : undefined
          }
          onSave={(value) => updateItem(routeSelected, value)}
          onAddExpiration={(value) => addExpirationForItem(routeSelected, value)}
        />
      ) : null}
    </div>
  );
}
